import React, { useState, useEffect } from 'react';
import { Popover, Card, Avatar, Typography, Descriptions, Spin, Tag } from 'antd';
import { GlobalOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { Federation } from '@/services/federation.service';
import FederationService from '@/services/federation.service';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';

const { Title, Text } = Typography;

interface FederationPopoverProps {
  federationId: number;
  children: React.ReactNode;
}

const FederationPopover: React.FC<FederationPopoverProps> = ({ federationId, children }) => {
  const [federation, setFederation] = useState<Federation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFederation = async () => {
      if (!federationId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await FederationService.getFederation(federationId);
        setFederation(data);
      } catch (err) {
        logger.error('Error fetching federation:', err);
        setError('Не удалось загрузить информацию о федерации');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFederation();
  }, [federationId]);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 10 }}>Загрузка информации...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#ff4d4f' }}>
          {error}
        </div>
      );
    }

    if (!federation) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Информация о федерации недоступна
        </div>
      );
    }

    return (
      <Card bordered={false} style={{ width: 350, padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Avatar 
            size={64} 
            src={federation.logo} 
            icon={<UserOutlined />} 
            style={{ backgroundColor: '#1890ff', marginRight: 16 }}
          />
          <div>
            <Title level={4} style={{ margin: 0 }}>{federation.name}</Title>
            <Text type="secondary">{federation.country}{federation.region ? `, ${federation.region}` : ''}</Text>
            <div>
              <Tag color={federation.status === 'active' ? 'success' : 'error'}>
                {federation.status === 'active' ? 'Активна' : 'Неактивна'}
              </Tag>
            </div>
          </div>
        </div>

        <Descriptions column={1} size="small" bordered>
          {federation.president && (
            <Descriptions.Item label="Президент">
              {federation.president}
            </Descriptions.Item>
          )}
          
          {federation.address && (
            <Descriptions.Item label={<><EnvironmentOutlined /> Адрес</>}>
              {federation.address}
            </Descriptions.Item>
          )}
          
          {federation.phone && (
            <Descriptions.Item label={<><PhoneOutlined /> Телефон</>}>
              {federation.phone}
            </Descriptions.Item>
          )}
          
          {federation.email && (
            <Descriptions.Item label={<><MailOutlined /> Email</>}>
              <a href={`mailto:${federation.email}`}>{federation.email}</a>
            </Descriptions.Item>
          )}
          
          {federation.website && (
            <Descriptions.Item label={<><GlobalOutlined /> Сайт</>}>
              <a href={federation.website} target="_blank" rel="noopener noreferrer">
                {federation.website}
              </a>
            </Descriptions.Item>
          )}
          
          {federation.description && (
            <Descriptions.Item label="Описание">
              {federation.description}
            </Descriptions.Item>
          )}
          
          <Descriptions.Item label={<><CalendarOutlined /> Дата создания</>}>
            {formatDate(federation.createdAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  return (
    <Popover 
      content={renderContent()} 
      title={null}
      trigger="hover"
      placement="right"
      overlayStyle={{ maxWidth: 400 }}
    >
      {children}
    </Popover>
  );
};

export default FederationPopover;
